import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Lightbulb, 
  Download, 
  Users, 
  Heart,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  userEmail: string;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  userEmail,
  onClose,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const welcomeSteps = [
    {
      title: 'Welcome to LightsHunt! 🎄',
      subtitle: 'Your holiday light journey begins here',
      content: (
        <div className="text-center space-y-4">
          <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <div>
            <p className="text-lg font-medium">Email Verified Successfully!</p>
            <p className="text-sm text-muted-foreground mt-1">
              Welcome <span className="font-medium">{userEmail}</span>
            </p>
          </div>
          <p className="text-muted-foreground">
            You're now part of the world's largest holiday light community. 
            Let's show you what you can do!
          </p>
        </div>
      ),
    },
    {
      title: 'What would you like to do first?',
      subtitle: 'Choose your path to get started',
      content: (
        <div className="grid gap-4">
          <WelcomeActionCard
            icon={<Lightbulb className="h-6 w-6 text-amber-600" />}
            title="Share Your Display"
            description="Add your holiday light display to help visitors find you"
            action={() => {
              navigate('/profile');
              onClose();
            }}
            primary
          />
          <WelcomeActionCard
            icon={<Download className="h-6 w-6 text-blue-600" />}
            title="Browse Sequences"
            description="Discover and download light sequences from creators worldwide"
            action={() => {
              navigate('/sequences');
              onClose();
            }}
          />
          <WelcomeActionCard
            icon={<Users className="h-6 w-6 text-purple-600" />}
            title="Explore Displays"
            description="Find amazing holiday displays in your area"
            action={() => {
              navigate('/displays');
              onClose();
            }}
          />
        </div>
      ),
    },
    {
      title: 'Pro Tips for Success',
      subtitle: 'Make the most of your LightsHunt experience',
      content: (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Heart className="h-5 w-5 text-red-500 mt-1 shrink-0" />
            <div>
              <p className="font-medium">Build Your Profile</p>
              <p className="text-sm text-muted-foreground">
                Add photos and details to your displays to attract more visitors
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-yellow-500 mt-1 shrink-0" />
            <div>
              <p className="font-medium">Share Quality Content</p>
              <p className="text-sm text-muted-foreground">
                Upload high-quality photos and sequences to get more engagement
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Users className="h-5 w-5 text-blue-500 mt-1 shrink-0" />
            <div>
              <p className="font-medium">Connect with Community</p>
              <p className="text-sm text-muted-foreground">
                Follow displays, rate sequences, and engage with other enthusiasts
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-linear-to-r from-primary/10 to-accent/10 rounded-lg">
            <p className="text-sm text-center">
              <strong>Ready to get started?</strong> Choose an action above or explore at your own pace!
            </p>
          </div>
        </div>
      ),
    },
  ];

  const currentStepData = welcomeSteps[currentStep];
  const isLastStep = currentStep === welcomeSteps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg sm:text-xl font-bold">
            {currentStepData.title}
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {currentStepData.subtitle}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator with step labels for better UX */}
        <div className="flex flex-col items-center gap-2 py-2">
          <div className="flex space-x-2">
            {welcomeSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-6 sm:w-8 rounded-full transition-all duration-300 ${
                  index <= currentStep ? 'bg-primary scale-110' : 'bg-muted'
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Step {currentStep + 1} of {welcomeSteps.length}
          </p>
        </div>

        <div className="py-4 sm:py-6">
          {currentStepData.content}
        </div>

        {/* Mobile-optimized button layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div className="hidden sm:flex space-x-2">
            {welcomeSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-8 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={handleSkip} 
              size="sm"
              className="flex-1 sm:flex-none"
            >
              Skip Tour
            </Button>
            <Button 
              onClick={handleNext} 
              size="sm"
              className="flex-1 sm:flex-none"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface WelcomeActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: () => void;
  primary?: boolean;
}

const WelcomeActionCard: React.FC<WelcomeActionCardProps> = ({
  icon,
  title,
  description,
  action,
  primary = false,
}) => {
  return (
    <Card 
      className={`cursor-pointer transition-all duration-200 hover:shadow-md active:scale-95 touch-manipulation ${
        primary ? 'ring-2 ring-primary/20 bg-primary/5 hover:ring-primary/30' : 'hover:scale-[1.02]'
      }`}
      onClick={action}
    >
      <CardHeader className="pb-4 sm:pb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 sm:p-3 rounded-lg ${primary ? 'bg-primary/10' : 'bg-muted'}`}>
            <div className="scale-110 sm:scale-100">
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm sm:text-base truncate">{title}</CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 line-clamp-2">
              {description}
            </CardDescription>
          </div>
          <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground shrink-0" />
        </div>
      </CardHeader>
    </Card>
  );
};

export default WelcomeModal;